/**
 * Project Overview metrics: load-once payloads with client-side Type / Range filters.
 */

import { formatNumber } from "../../../../utils/common/formatting/numbers";
import {
  buildFullMonthSuggestionsFromEntries,
  formatShortMonthDay,
  getCalendarDayCount,
  getDateInLocalTZFromDateStr,
  getShiftedDate,
  toLocalCalendarDay,
  toLocalDateKey,
  toProjectDateKey,
  today,
} from "../../../../utils/datetime";

/** Sentinel selection key for project-lifetime metrics / All type filters. */
export const OVERVIEW_METRICS_RANGE_ALL = "all";
/** Selection key when the user picks an explicit start/end date range. */
export const OVERVIEW_METRICS_RANGE_CUSTOM = "custom";
export const OVERVIEW_METRICS_TYPE_ALL = "all";

/** Concrete Pours Type dropdown base options (labels get counts appended). */
export const CONCRETE_POURS_TYPE_OPTIONS = [
  { value: "all", label: "All" },
  { value: "past", label: "Past Only" },
  { value: "dispatched", label: "Dispatched Only" },
  { value: "reviewed", label: "Reviewed Only" },
];

/**
 * Unbounded project-lifetime metrics selection (no start/end).
 *
 * @returns {{key: string, start: null, end: null}}
 */
export function getAllOverviewMetricsSelection() {
  return {
    key: OVERVIEW_METRICS_RANGE_ALL,
    start: null,
    end: null,
  };
}

/**
 * Bounded lookback window ending today — used when materializing an open range
 * for chevron shifts (daily: −1 month, weekly: −1 year).
 *
 * @param {"daily"|"weekly"} granularity
 * @returns {{key: string, start: string, end: string}}
 */
export function getOverviewMetricsSelectionForGranularity(granularity) {
  const endDate = toLocalCalendarDay(today());
  const startDate = toLocalCalendarDay(endDate);
  if (granularity === "weekly") {
    startDate.setFullYear(startDate.getFullYear() - 1);
  } else {
    startDate.setMonth(startDate.getMonth() - 1);
  }
  return {
    key: OVERVIEW_METRICS_RANGE_CUSTOM,
    start: toLocalDateKey(startDate),
    end: toLocalDateKey(endDate),
  };
}

/**
 * Infer a blank JobNumber ``date_start`` / ``date_end`` from labor + concrete activity.
 *
 * - ``start``: earliest day with any hours or pour row.
 * - ``end``: later of (latest activity day, today).
 *
 * @param {Object|null|undefined} project - JobNumber / project row (reserved for job context).
 * @param {"start"|"end"} bound - Which blank bound to resolve.
 * @param {Array<{date?: string}>|null|undefined} [entries] - Combined payout-hour + pour rows.
 * @returns {string|null} ``YYYY-MM-DD``, or null when a start cannot be inferred.
 */
export function resolveBlankOverviewMetricsDate(project, bound, entries = null) {
  if (!project || (bound !== "start" && bound !== "end")) {
    return null;
  }

  let earliest = null;
  let latest = null;
  (entries || []).forEach((entry) => {
    const dateKey = toProjectDateKey(entry?.date);
    if (!dateKey) return;
    if (!earliest || dateKey < earliest) earliest = dateKey;
    if (!latest || dateKey > latest) latest = dateKey;
  });

  if (bound === "start") {
    return earliest;
  }

  // Blank end: later of last activity day and today (today when no activity).
  const todayKey = toLocalDateKey(toLocalCalendarDay(today()));
  if (!latest || todayKey > latest) {
    return todayKey;
  }
  return latest;
}

/**
 * Effective Entire Project bounds: saved JobNumber dates, with blanks filled
 * from labor/concrete activity when ``entries`` are provided.
 *
 * @param {Object|null|undefined} [project] - Project API row.
 * @param {Array<{date?: string}>|null|undefined} [entries] - Combined activity rows for blanks.
 * @returns {{start: string|null, end: string|null}}
 */
export function getEntireProjectMetricsBounds(project = null, entries = null) {
  let start = toProjectDateKey(project?.date_start);
  let end = toProjectDateKey(project?.date_end);
  if (!start) {
    start = resolveBlankOverviewMetricsDate(project, "start", entries);
  }
  if (!end) {
    end = resolveBlankOverviewMetricsDate(project, "end", entries);
  }
  return { start: start || null, end: end || null };
}

/**
 * Default metrics range from {@link getEntireProjectMetricsBounds}.
 *
 * @param {Object|null|undefined} [project] - Project API row.
 * @param {Array<{date?: string}>|null|undefined} [entries] - Combined activity rows for blanks.
 * @returns {{key: string, start: string|null, end: string|null}}
 */
export function getDefaultOverviewMetricsSelection(project = null, entries = null) {
  const { start, end } = getEntireProjectMetricsBounds(project, entries);
  return selectionFromDateBounds(start, end);
}

/**
 * Build a metrics range selection from optional YYYY-MM-DD bounds.
 * Either bound may be blank (open-ended); both blank → All.
 *
 * @param {string|null|undefined} start - Inclusive start date key.
 * @param {string|null|undefined} end - Inclusive end date key.
 * @returns {{key: string, start: string|null, end: string|null}}
 */
export function selectionFromDateBounds(start, end) {
  const startKey = start || null;
  const endKey = end || null;
  if (!startKey && !endKey) {
    return getAllOverviewMetricsSelection();
  }
  if (startKey && endKey && startKey > endKey) {
    return {
      key: OVERVIEW_METRICS_RANGE_CUSTOM,
      start: endKey,
      end: startKey,
    };
  }
  return {
    key: OVERVIEW_METRICS_RANGE_CUSTOM,
    start: startKey,
    end: endKey,
  };
}

/**
 * Inclusive day span for a bounded metrics range (null when open-ended).
 *
 * @param {{start: string|null, end: string|null}|null|undefined} range
 * @returns {number|null}
 */
export function getOverviewMetricsRangeSpanDays(range) {
  if (!range?.start || !range?.end) return null;
  return getCalendarDayCount(range.start, range.end);
}

/**
 * Shift the metrics window by one day (daily) or one week (weekly).
 * Open bounds are materialized first (earliest activity day and/or today) so
 * scrolling can continue indefinitely in either direction.
 *
 * @param {{start: string|null, end: string|null}|null|undefined} range
 * @param {"daily"|"weekly"} granularity
 * @param {-1|1} direction - ``-1`` previous, ``1`` next.
 * @param {Object} [options]
 * @param {string|null} [options.earliestDateKey] - Earliest activity day when start is open.
 * @returns {{key: string, start: string, end: string}}
 */
export function shiftOverviewMetricsRange(
  range,
  granularity,
  direction,
  { earliestDateKey = null } = {}
) {
  let startKey = range?.start || null;
  let endKey = range?.end || null;
  if (!endKey) {
    endKey = toLocalDateKey(toLocalCalendarDay(today()));
  }
  if (!startKey) {
    // Prefer full activity span; fall back to the granularity lookback window.
    startKey =
      earliestDateKey ||
      getOverviewMetricsSelectionForGranularity(granularity).start;
  }
  const stepDays = granularity === "weekly" ? 7 : 1;
  const delta = direction * stepDays;
  const start = toLocalDateKey(
    getShiftedDate(getDateInLocalTZFromDateStr(startKey), delta)
  );
  const end = toLocalDateKey(
    getShiftedDate(getDateInLocalTZFromDateStr(endKey), delta)
  );
  return selectionFromDateBounds(start, end);
}

/**
 * Round numeric metric values to two decimal places (matches backend helpers).
 *
 * @param {number} value
 * @returns {number}
 */
function roundMetric(value) {
  return Math.round((Number(value) || 0) * 100) / 100;
}

/**
 * Whether an ISO date key is inside the metrics range selection.
 * Missing start and/or end means open-ended on that side; both missing = All.
 *
 * @param {string} dateKey - YYYY-MM-DD.
 * @param {{start: string|null, end: string|null}|null} range
 * @returns {boolean}
 */
function isMetricsDateInRange(dateKey, range) {
  if (!range?.start && !range?.end) return true;
  if (range.start && dateKey < range.start) return false;
  if (range.end && dateKey > range.end) return false;
  return true;
}

/**
 * Count unique workers per union craft within the selected Range.
 *
 * @param {Array<Object>|null|undefined} entries - Raw payout-hour entries.
 * @param {{start: string|null, end: string|null}|null} range
 * @returns {Object<string, number>} Keys ``all`` and WorkerType id strings.
 */
export function countWorkerHoursByType(entries, range = null) {
  const workersByType = { [OVERVIEW_METRICS_TYPE_ALL]: new Set() };

  (entries || []).forEach((entry) => {
    if (!entry?.date || !isMetricsDateInRange(entry.date, range)) return;
    if (entry.workerId == null) return;

    workersByType[OVERVIEW_METRICS_TYPE_ALL].add(entry.workerId);
    if (entry.workerTypeId == null) return;

    const typeKey = String(entry.workerTypeId);
    if (!workersByType[typeKey]) {
      workersByType[typeKey] = new Set();
    }
    workersByType[typeKey].add(entry.workerId);
  });

  const counts = {};
  Object.keys(workersByType).forEach((key) => {
    counts[key] = workersByType[key].size;
  });
  return counts;
}

/**
 * Count concrete pours per Type filter within the selected Range.
 *
 * @param {Array<Object>|null|undefined} entries - Raw concrete pour entries.
 * @param {{start: string|null, end: string|null}|null} range
 * @returns {{all: number, past: number, dispatched: number, reviewed: number}}
 */
export function countConcretePoursByType(entries, range = null) {
  const todayKey = toLocalDateKey(today());
  const counts = {
    all: 0,
    past: 0,
    dispatched: 0,
    reviewed: 0,
  };

  (entries || []).forEach((entry) => {
    if (!entry?.date || !isMetricsDateInRange(entry.date, range)) return;

    counts.all += 1;
    if (entry.date < todayKey) {
      counts.past += 1;
    }
    if (entry.dispatchStage && entry.dispatchStage !== "Draft") {
      counts.dispatched += 1;
    }
    if (entry.dispatchStage === "Reviewed") {
      counts.reviewed += 1;
    }
  });

  return counts;
}

/**
 * Build Worker Hours Type dropdown options with counts for the current Range.
 *
 * @param {Array<{id: number, type: string}>|null|undefined} workerTypes
 * @param {Array<Object>|null|undefined} entries - Raw payout-hour entries.
 * @param {{start: string|null, end: string|null}|null} range
 * @returns {Array<{value: string, label: string}>}
 */
/**
 * Normalize a Type selection to an array of concrete keys (no ``all``).
 * Empty array means All types.
 *
 * @param {string|string[]|null|undefined} typeKeys
 * @returns {string[]}
 */
export function normalizeTypeKeys(typeKeys) {
  const list = Array.isArray(typeKeys)
    ? typeKeys
    : typeKeys && typeKeys !== OVERVIEW_METRICS_TYPE_ALL
      ? [typeKeys]
      : [];
  return list
    .map((key) => String(key))
    .filter((key) => key && key !== OVERVIEW_METRICS_TYPE_ALL);
}

/**
 * Build Worker Hours Type multi-select options with counts for the current Range.
 * Empty selection in the UI means All (no ``all`` option).
 *
 * @param {Array<{id: number, type: string}>|null|undefined} workerTypes
 * @param {Array<Object>|null|undefined} entries - Raw payout-hour entries.
 * @param {{start: string|null, end: string|null}|null} range
 * @returns {Array<{value: string, label: string, count: number}>}
 */
export function buildWorkerHoursTypeOptions(
  workerTypes,
  entries = null,
  range = null
) {
  const counts = countWorkerHoursByType(entries, range);
  const options = [];
  (workerTypes || []).forEach((workerType) => {
    if (workerType?.id == null) return;
    const typeKey = String(workerType.id);
    const name = workerType.type || `Type ${workerType.id}`;
    options.push({
      value: typeKey,
      label: name,
      count: counts[typeKey] || 0,
    });
  });
  return options;
}

/**
 * Build Concrete Pours Type dropdown options with counts for the current Range.
 *
 * @param {Array<Object>|null|undefined} entries - Raw concrete pour entries.
 * @param {{start: string|null, end: string|null}|null} range
 * @returns {Array<{value: string, label: string}>}
 */
export function buildConcretePoursTypeOptions(entries = null, range = null) {
  const counts = countConcretePoursByType(entries, range);
  return CONCRETE_POURS_TYPE_OPTIONS.map((option) => ({
    value: option.value,
    label:
      option.value === OVERVIEW_METRICS_TYPE_ALL
        ? option.label
        : `${option.label} (${counts[option.value] || 0})`,
  }));
}

/**
 * Whether a worker-hours entry matches any selected Type key (OR).
 * Empty ``typeKeys`` = All.
 *
 * @param {Object} entry
 * @param {string|string[]|null|undefined} typeKeys
 * @returns {boolean}
 */
function workerHoursEntryMatchesTypes(entry, typeKeys) {
  const keys = normalizeTypeKeys(typeKeys);
  if (!keys.length) return true;
  return keys.some(
    (typeKey) => Number(entry?.workerTypeId) === Number(typeKey)
  );
}

/**
 * Whether a concrete-pour entry matches a single concrete Type filter key.
 *
 * @param {Object} entry
 * @param {string} typeKey - ``past`` | ``dispatched`` | ``reviewed``.
 * @returns {boolean}
 */
function concretePoursEntryMatchesType(entry, typeKey) {
  const todayKey = toLocalDateKey(today());
  if (typeKey === "past") return entry?.date < todayKey;
  if (typeKey === "dispatched") return entry?.dispatchStage !== "Draft";
  if (typeKey === "reviewed") return entry?.dispatchStage === "Reviewed";
  return true;
}

/**
 * Whether the metrics range matches the stored Entire Project bounds.
 *
 * @param {{start?: string|null, end?: string|null}|null|undefined} range
 * @param {{start?: string|null, end?: string|null}|null|undefined} entireBounds
 *   Stored bounds from {@link getEntireProjectMetricsBounds}.
 * @returns {boolean}
 */
export function isEntireProjectMetricsRange(range, entireBounds) {
  const start = toProjectDateKey(entireBounds?.start);
  const end = toProjectDateKey(entireBounds?.end);
  if (!start || !end) {
    return false;
  }
  return (
    toProjectDateKey(range?.start) === start &&
    toProjectDateKey(range?.end) === end
  );
}

/**
 * Format a metrics range for the card title, e.g. ``Apr 16 - Apr 30, 2026``.
 *
 * When the range matches the stored Entire Project bounds, returns
 * ``Entire Project``.
 *
 * @param {{start?: string|null, end?: string|null}|null|undefined} range
 * @param {{start?: string|null, end?: string|null}|null|undefined} [entireBounds]
 *   Stored bounds for Entire Project label matching.
 * @returns {string}
 */
export function formatOverviewMetricsRangeTitle(range, entireBounds = null) {
  if (isEntireProjectMetricsRange(range, entireBounds)) {
    return "Entire Project";
  }

  const startKey = range?.start || null;
  const endKey = range?.end || null;
  if (!startKey && !endKey) {
    return "All dates";
  }

  const startDate = startKey ? getDateInLocalTZFromDateStr(startKey) : null;
  const endDate = endKey ? getDateInLocalTZFromDateStr(endKey) : null;
  const formatFull = (date) =>
    date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  const formatShort = (date) =>
    date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });

  if (startDate && endDate) {
    if (startDate.getFullYear() === endDate.getFullYear()) {
      return `${formatShort(startDate)} - ${formatFull(endDate)}`;
    }
    return `${formatFull(startDate)} - ${formatFull(endDate)}`;
  }
  if (startDate) {
    return `${formatFull(startDate)} onward`;
  }
  return `Until ${formatFull(endDate)}`;
}

/**
 * Entire-project chip for the metrics date picker from stored bounds.
 *
 * @param {{start?: string|null, end?: string|null}|null|undefined} entireBounds
 *   Stored bounds from {@link getEntireProjectMetricsBounds}.
 * @returns {{key: string, start: string, end: string, label: string, className: string}|null}
 */
export function buildEntireProjectRangeSuggestion(entireBounds) {
  const start = toProjectDateKey(entireBounds?.start);
  const end = toProjectDateKey(entireBounds?.end);
  if (!start || !end) {
    return null;
  }
  return {
    key: "entire-project",
    start,
    end,
    label: "Entire Project",
    // Darker than month chips so Entire Project stands out in the presets list.
    className: "!bg-teal-800 hover:!bg-teal-900",
  };
}

/**
 * Prepend the Entire Project chip when stored bounds are complete.
 *
 * @param {{start?: string|null, end?: string|null}|null|undefined} entireBounds
 * @param {Array<Object>} monthSuggestions - Full-month chips from entries.
 * @returns {Array<Object>}
 */
function withEntireProjectSuggestion(entireBounds, monthSuggestions) {
  const entire = buildEntireProjectRangeSuggestion(entireBounds);
  if (!entire) {
    return monthSuggestions || [];
  }
  return [entire, ...(monthSuggestions || [])];
}

/**
 * Derive Worker Hours Overview Type options + start-month suggestions.
 *
 * Chart labels stay unprefixed (``All``) when 0 or 2+ types are selected so
 * multi-type views keep a single amalgamated series without busy legends.
 *
 * @param {Object} summary - ``workerHoursSummary`` from ProjectPageContext.
 * @param {{start?: string|null, end?: string|null}|null|undefined} [entireBounds]
 *   Stored Entire Project bounds for the preset chip.
 * @returns {{typeKeys: string[], typeLabel: string, chartTypeLabel: string, typeOptions: Array<Object>, range: Object, startSuggestions: Array}}
 */
export function buildWorkerHoursOverviewUi(summary, entireBounds = null) {
  const typeKeys = normalizeTypeKeys(summary?.type);
  const range = summary?.range ?? getAllOverviewMetricsSelection();
  // Single type keeps a prefix; All / multi use plain metric + chart labels.
  const typeLabel = formatWorkerHoursTypeLabel(
    typeKeys,
    summary?.workerTypes
  );
  return {
    typeKeys,
    typeLabel,
    chartTypeLabel: typeLabel,
    typeOptions: buildWorkerHoursTypeOptions(
      summary?.workerTypes,
      summary?.entries,
      summary?.range
    ),
    range,
    startSuggestions: withEntireProjectSuggestion(
      entireBounds,
      buildFullMonthSuggestionsFromEntries(summary?.entries, {
        includeEntry: (entry) => workerHoursEntryMatchesTypes(entry, typeKeys),
      })
    ),
  };
}

/**
 * Derive Concrete Pours Overview Type options + full-month suggestions.
 *
 * @param {Object} summary - ``concretePoursSummary`` from ProjectPageContext.
 * @param {{start?: string|null, end?: string|null}|null|undefined} [entireBounds]
 *   Stored Entire Project bounds for the preset chip.
 * @returns {{typeKey: string, typeLabel: string, typeOptions: Array<Object>, range: Object, startSuggestions: Array}}
 */
export function buildConcretePoursOverviewUi(summary, entireBounds = null) {
  const typeKey = summary?.type || OVERVIEW_METRICS_TYPE_ALL;
  const range = summary?.range ?? getAllOverviewMetricsSelection();
  return {
    typeKey,
    typeLabel: getConcretePoursTypeLabel(typeKey),
    typeOptions: buildConcretePoursTypeOptions(
      summary?.entries,
      summary?.range
    ),
    range,
    startSuggestions: withEntireProjectSuggestion(
      entireBounds,
      buildFullMonthSuggestionsFromEntries(summary?.entries, {
        includeEntry: (entry) =>
          !typeKey ||
          typeKey === OVERVIEW_METRICS_TYPE_ALL ||
          concretePoursEntryMatchesType(entry, typeKey),
      })
    ),
  };
}

/**
 * Aggregate worker-hour entries for the current Type + Range selection.
 *
 * @param {Array<Object>|null|undefined} entries - Rows from getProjectPayoutHours.
 * @param {{start: string|null, end: string|null}|null} range - Date range selection.
 * @param {string|string[]} [typeKeys=[]] - Selected WorkerType ids; empty = All.
 * @returns {{totalHours: number, regularHours: number, overtimeHours: number, doubleTimeHours: number, workerCount: number, days: Array<Object>}}
 */
export function aggregateWorkerHoursEntries(
  entries,
  range = null,
  typeKeys = []
) {
  let regularHours = 0;
  let overtimeHours = 0;
  let doubleTimeHours = 0;
  const workerIds = new Set();
  const daysByDate = {};

  (entries || []).forEach((entry) => {
    const dateKey = entry?.date;
    if (!dateKey || !isMetricsDateInRange(dateKey, range)) return;
    if (!workerHoursEntryMatchesTypes(entry, typeKeys)) return;

    const rt = Number(entry.regularHours) || 0;
    const ot = Number(entry.overtimeHours) || 0;
    const dt = Number(entry.doubleTimeHours) || 0;
    regularHours += rt;
    overtimeHours += ot;
    doubleTimeHours += dt;

    if (entry.workerId != null) {
      workerIds.add(entry.workerId);
    }

    if (!daysByDate[dateKey]) {
      daysByDate[dateKey] = {
        date: dateKey,
        regularHours: 0,
        overtimeHours: 0,
        doubleTimeHours: 0,
        workerIds: new Set(),
      };
    }
    daysByDate[dateKey].regularHours += rt;
    daysByDate[dateKey].overtimeHours += ot;
    daysByDate[dateKey].doubleTimeHours += dt;
    if (entry.workerId != null) {
      daysByDate[dateKey].workerIds.add(entry.workerId);
    }
  });

  const days = Object.keys(daysByDate)
    .sort()
    .map((key) => {
      const workerIds = Array.from(daysByDate[key].workerIds);
      return {
        date: daysByDate[key].date,
        regularHours: roundMetric(daysByDate[key].regularHours),
        overtimeHours: roundMetric(daysByDate[key].overtimeHours),
        doubleTimeHours: roundMetric(daysByDate[key].doubleTimeHours),
        workerCount: workerIds.length,
        // Kept for weekly chart rollups (unique workers across the week).
        workerIds,
      };
    });

  return {
    regularHours: roundMetric(regularHours),
    overtimeHours: roundMetric(overtimeHours),
    doubleTimeHours: roundMetric(doubleTimeHours),
    totalHours: roundMetric(regularHours + overtimeHours + doubleTimeHours),
    workerCount: workerIds.size,
    days,
  };
}

/** Fallback mix label when ConcreteAssignment.mix is null. */
export const CONCRETE_MIX_UNKNOWN = "No mix";

/**
 * Round a mix→yards map to metric precision.
 *
 * @param {Object<string, number>} yardsByMix
 * @returns {Object<string, number>}
 */
function roundYardsByMix(yardsByMix) {
  const rounded = {};
  Object.keys(yardsByMix || {}).forEach((mixKey) => {
    rounded[mixKey] = roundMetric(yardsByMix[mixKey]);
  });
  return rounded;
}

/**
 * Aggregate concrete pours for the chart: full Range volume, split by Type match.
 *
 * Metrics tiles still use {@link aggregateConcretePoursEntries}. The chart uses
 * these days so out-of-filter volume can be drawn gray beside in-filter color.
 *
 * @param {Array<Object>|null|undefined} entries - Rows from getProjectConcretePours.
 * @param {{start: string|null, end: string|null}|null} range - Date range selection.
 * @param {string} [typeKey=OVERVIEW_METRICS_TYPE_ALL] - Exclusive type filter.
 * @returns {Array<{date: string, yards: number, pourCount: number, yardsByMix: Object, yardsByMixInFilter: Object, yardsByMixOutFilter: Object}>}
 */
export function aggregateConcretePoursChartDays(
  entries,
  range = null,
  typeKey = OVERVIEW_METRICS_TYPE_ALL
) {
  const filterActive =
    Boolean(typeKey) && typeKey !== OVERVIEW_METRICS_TYPE_ALL;
  const daysByDate = {};

  (entries || []).forEach((entry) => {
    const dateKey = entry?.date;
    if (!dateKey || !isMetricsDateInRange(dateKey, range)) return;

    const yards = Number(entry.yards) || 0;
    const mixKey = entry.designNumber || CONCRETE_MIX_UNKNOWN;
    const inFilter =
      !filterActive || concretePoursEntryMatchesType(entry, typeKey);

    if (!daysByDate[dateKey]) {
      daysByDate[dateKey] = {
        date: dateKey,
        yards: 0,
        pourCount: 0,
        yardsByMix: {},
        yardsByMixInFilter: {},
        yardsByMixOutFilter: {},
      };
    }

    const bucket = daysByDate[dateKey];
    bucket.yards += yards;
    bucket.pourCount += 1;
    bucket.yardsByMix[mixKey] = (bucket.yardsByMix[mixKey] || 0) + yards;
    if (inFilter) {
      bucket.yardsByMixInFilter[mixKey] =
        (bucket.yardsByMixInFilter[mixKey] || 0) + yards;
    } else {
      bucket.yardsByMixOutFilter[mixKey] =
        (bucket.yardsByMixOutFilter[mixKey] || 0) + yards;
    }
  });

  return Object.keys(daysByDate)
    .sort()
    .map((key) => {
      const bucket = daysByDate[key];
      return {
        date: bucket.date,
        yards: roundMetric(bucket.yards),
        pourCount: bucket.pourCount,
        yardsByMix: roundYardsByMix(bucket.yardsByMix),
        yardsByMixInFilter: roundYardsByMix(bucket.yardsByMixInFilter),
        yardsByMixOutFilter: roundYardsByMix(bucket.yardsByMixOutFilter),
      };
    });
}

/**
 * Aggregate concrete-pour entries for the current Type + Range selection.
 *
 * @param {Array<Object>|null|undefined} entries - Rows from getProjectConcretePours.
 * @param {{start: string|null, end: string|null}|null} range - Date range selection.
 * @param {string} [typeKey=OVERVIEW_METRICS_TYPE_ALL] - Exclusive type filter.
 * @returns {{totalVolume: number, pourCount: number, averagePerPour: number, lastPourDate: string|null, days: Array<Object>, mixDesigns: string[]}}
 */
export function aggregateConcretePoursEntries(
  entries,
  range = null,
  typeKey = OVERVIEW_METRICS_TYPE_ALL
) {
  let totalVolume = 0;
  let pourCount = 0;
  let lastPourDate = null;
  const daysByDate = {};
  const mixDesigns = new Set();

  (entries || []).forEach((entry) => {
    const dateKey = entry?.date;
    if (!dateKey || !isMetricsDateInRange(dateKey, range)) {
      return;
    }
    if (
      typeKey &&
      typeKey !== OVERVIEW_METRICS_TYPE_ALL &&
      !concretePoursEntryMatchesType(entry, typeKey)
    ) {
      return;
    }

    const yards = Number(entry.yards) || 0;
    const mixKey = entry.designNumber || CONCRETE_MIX_UNKNOWN;
    totalVolume += yards;
    pourCount += 1;
    mixDesigns.add(mixKey);
    if (!lastPourDate || dateKey > lastPourDate) {
      lastPourDate = dateKey;
    }

    if (!daysByDate[dateKey]) {
      daysByDate[dateKey] = {
        date: dateKey,
        yards: 0,
        pourCount: 0,
        yardsByMix: {},
      };
    }
    daysByDate[dateKey].yards += yards;
    daysByDate[dateKey].pourCount += 1;
    daysByDate[dateKey].yardsByMix[mixKey] =
      (daysByDate[dateKey].yardsByMix[mixKey] || 0) + yards;
  });

  const days = Object.keys(daysByDate)
    .sort()
    .map((key) => {
      const bucket = daysByDate[key];
      const yardsByMix = {};
      Object.keys(bucket.yardsByMix).forEach((mixKey) => {
        yardsByMix[mixKey] = roundMetric(bucket.yardsByMix[mixKey]);
      });
      return {
        date: bucket.date,
        yards: roundMetric(bucket.yards),
        pourCount: bucket.pourCount,
        yardsByMix,
      };
    });

  return {
    totalVolume: roundMetric(totalVolume),
    pourCount,
    averagePerPour: pourCount > 0 ? roundMetric(totalVolume / pourCount) : 0,
    lastPourDate,
    days,
    mixDesigns: Array.from(mixDesigns).sort((a, b) => a.localeCompare(b)),
  };
}

/**
 * Resolve a single Worker Hours Type label.
 *
 * @param {string} typeKey - ``all`` or WorkerType id.
 * @param {Array<{id: number, type: string}>|null|undefined} workerTypes
 * @returns {string}
 */
export function getWorkerHoursTypeLabel(typeKey, workerTypes) {
  if (!typeKey || typeKey === OVERVIEW_METRICS_TYPE_ALL) {
    return "All";
  }
  const match = (workerTypes || []).find(
    (workerType) => String(workerType.id) === String(typeKey)
  );
  return match?.type || "All";
}

/**
 * Resolve Worker Hours Type label for metric / chart prefixes.
 * Empty or multiple selections → ``All`` (plain unprefixed labels).
 *
 * @param {string|string[]|null|undefined} typeKeys
 * @param {Array<{id: number, type: string}>|null|undefined} workerTypes
 * @returns {string}
 */
export function formatWorkerHoursTypeLabel(typeKeys, workerTypes) {
  const keys = normalizeTypeKeys(typeKeys);
  if (keys.length !== 1) return "All";
  return getWorkerHoursTypeLabel(keys[0], workerTypes);
}

/**
 * Resolve a single Concrete Pours Type label.
 *
 * @param {string} typeKey - Concrete type filter key.
 * @returns {string}
 */
export function getConcretePoursTypeLabel(typeKey) {
  if (!typeKey || typeKey === OVERVIEW_METRICS_TYPE_ALL) {
    return "All";
  }
  const match = CONCRETE_POURS_TYPE_OPTIONS.find(
    (option) => option.value === typeKey
  );
  return match?.label || "All";
}

/**
 * Prefix a label with the active Type name (metrics tiles and chart datasets).
 * When Type is All, return the base label only — the filter is implied.
 *
 * @param {string} typeLabel
 * @param {string} baseLabel
 * @returns {string}
 */
export function withTypePrefix(typeLabel, baseLabel) {
  if (!typeLabel || typeLabel === "All") {
    return baseLabel;
  }
  return `${typeLabel} ${baseLabel}`;
}

/**
 * Build the four Worker Hours overview tiles from aggregated totals.
 *
 * @param {Object|null} data - Aggregated worker-hours payload.
 * @param {string|string[]} [typeKeys=[]] - Active Type filter(s); empty = All.
 * @param {Array<{id: number, type: string}>|null|undefined} [workerTypes]
 * @param {boolean} [isEntireProject=false] - Range matches project date_start–date_end.
 * @returns {Array<{value: string, label: string, accent?: string}>}
 */
export function buildWorkerHoursMetrics(
  data,
  typeKeys = [],
  workerTypes = null,
  isEntireProject = false
) {
  const totalHours = Number(data?.totalHours) || 0;
  const regularHours = Number(data?.regularHours) || 0;
  const overtimeHours = Number(data?.overtimeHours) || 0;
  const doubleTimeHours = Number(data?.doubleTimeHours) || 0;
  const workerCount = Number(data?.workerCount) || 0;
  const typeLabel = formatWorkerHoursTypeLabel(typeKeys, workerTypes);
  const totalLabel = isEntireProject
    ? "Project Total Hours"
    : "Hours In Range";

  return [
    {
      value: formatNumber(totalHours, 1, true, true),
      label: withTypePrefix(typeLabel, totalLabel),
    },
    {
      value: formatNumber(regularHours, 1, true, true),
      label: withTypePrefix(typeLabel, "Regular Time"),
    },
    {
      value: formatNumber(overtimeHours, 1, true, true),
      label: withTypePrefix(typeLabel, "Overtime"),
      accent: "overtime",
    },
    {
      value: formatNumber(doubleTimeHours, 1, true, true),
      label: withTypePrefix(typeLabel, "Double Time"),
      accent: "doubletime",
    },
    {
      value: formatNumber(workerCount, 0, true, true),
      label: withTypePrefix(typeLabel, "Workers"),
    },
  ];
}

/**
 * Placeholder metrics shown while payout hours are loading.
 *
 * @param {string|string[]} [typeKeys=[]] - Active Type filter(s); empty = All.
 * @param {Array<{id: number, type: string}>|null|undefined} [workerTypes]
 * @param {boolean} [isEntireProject=false] - Range matches project date_start–date_end.
 * @returns {Array<{value: string, label: string}>}
 */
export function getLoadingWorkerHoursMetrics(
  typeKeys = [],
  workerTypes = null,
  isEntireProject = false
) {
  const typeLabel = formatWorkerHoursTypeLabel(typeKeys, workerTypes);
  const totalLabel = isEntireProject
    ? "Project Total Hours"
    : "Hours In Range";
  return [
    { value: "…", label: withTypePrefix(typeLabel, totalLabel) },
    { value: "…", label: withTypePrefix(typeLabel, "Regular Time") },
    {
      value: "…",
      label: withTypePrefix(typeLabel, "Overtime"),
      accent: "overtime",
    },
    {
      value: "…",
      label: withTypePrefix(typeLabel, "Double Time"),
      accent: "doubletime",
    },
    { value: "…", label: withTypePrefix(typeLabel, "Workers") },
  ];
}

/**
 * Build the four Concrete Pours overview tiles from aggregated totals.
 *
 * @param {Object|null} data - Aggregated concrete-pours payload.
 * @param {string} [typeKey=OVERVIEW_METRICS_TYPE_ALL] - Active Type filter.
 * @param {boolean} [isEntireProject=false] - Range matches project date_start–date_end.
 * @returns {Array<{value: string, label: string}>}
 */
export function buildConcretePoursMetrics(
  data,
  typeKey = OVERVIEW_METRICS_TYPE_ALL,
  isEntireProject = false
) {
  const totalVolume = Number(data?.totalVolume) || 0;
  const pourCount = Number(data?.pourCount) || 0;
  const averagePerPour = Number(data?.averagePerPour) || 0;
  const lastPourDate = data?.lastPourDate
    ? formatShortMonthDay(getDateInLocalTZFromDateStr(data.lastPourDate))
    : "—";
  const typeLabel = getConcretePoursTypeLabel(typeKey);
  const totalLabel = isEntireProject
    ? "Project Poured Volume"
    : "Volume In Range";

  return [
    {
      value: `${formatNumber(totalVolume, 1, true, true)} CY`,
      label: withTypePrefix(typeLabel, totalLabel),
    },
    {
      value: formatNumber(pourCount, 0, true, true),
      label: withTypePrefix(typeLabel, "Pours"),
    },
    {
      value: `${formatNumber(averagePerPour, 1, true, true)} CY`,
      label: withTypePrefix(typeLabel, "Avg / pour"),
    },
    {
      value: lastPourDate,
      label: withTypePrefix(typeLabel, "Last pour"),
    },
  ];
}

/**
 * Placeholder metrics shown while concrete pours are loading.
 *
 * @param {string} [typeKey=OVERVIEW_METRICS_TYPE_ALL] - Active Type filter.
 * @param {boolean} [isEntireProject=false] - Range matches project date_start–date_end.
 * @returns {Array<{value: string, label: string}>}
 */
export function getLoadingConcretePoursMetrics(
  typeKey = OVERVIEW_METRICS_TYPE_ALL,
  isEntireProject = false
) {
  const typeLabel = getConcretePoursTypeLabel(typeKey);
  const totalLabel = isEntireProject
    ? "Project Poured Volume"
    : "Volume In Range";
  return [
    { value: "…", label: withTypePrefix(typeLabel, totalLabel) },
    { value: "…", label: withTypePrefix(typeLabel, "Pours") },
    { value: "…", label: withTypePrefix(typeLabel, "Avg / pour") },
    { value: "…", label: withTypePrefix(typeLabel, "Last pour") },
  ];
}
